import { useState } from 'react';

import { TFundingRequirement } from '@helpers/FundingRequirements/fundingRequirements.types';
import { getFundingRequirement } from '@helpers/FundingRequirements/getFundingRequirement';
import { getIdVerificationPersonInReview } from '@helpers/FundingRequirements/getIdVerificationPersonInReview';
import { getIdVerificationPersonToVerify } from '@helpers/FundingRequirements/getIdVerificationPersonToVerify';
import { getIdVerificationPersonWithError } from '@helpers/FundingRequirements/getIdVerificationPersonWithError';
import { logError, TAGS } from '@iwLogger/IwDataDogLogger';
import { maintainStateKey } from '@iwRouter/LocationHelper';
import { Redirect, useLocation } from 'react-router-dom';

import { IdCheckComplete } from './IdCheckComplete';
import { IdCheckSmsSendError } from './IdCheckSmsSendError';
import { IdCheckSmsSent } from './IdCheckSmsSent';
import { MultiplePeopleIdCheck } from './MultiplePeopleIdCheck';
import { SendIdCheckSms } from './SendIdCheckSms';
import { useGetFundingRequirements } from '../../../../hooks/useGetFundingRequirements';
import { FUNDING_REQUIREMENTS_LIST_PATH } from '../../../../requirementStepPaths';

const IN_REVIEW_POLL_INTERVAL_MS = 5000;

// The customer finishes their ID check on their phone, so poll while it is in review
// for this page to move on by itself.
const pollWhileInReview = (data?: { requirements: TFundingRequirement[] }) => {
    const requirement =
        data && getFundingRequirement(data.requirements, 'id_verification');
    return requirement && getIdVerificationPersonInReview(requirement)
        ? IN_REVIEW_POLL_INTERVAL_MS
        : false;
};

export const IdVerificationPage = () => {
    const { search } = useLocation();
    const { requirements } = useGetFundingRequirements({
        refetchInterval: pollWhileInReview
    });
    const requirement = getFundingRequirement(requirements, 'id_verification');
    const [sentNumber, setSentNumber] = useState<string>();
    const [isResending, setIsResending] = useState(false);
    const [isRetryingFailedCheck, setIsRetryingFailedCheck] = useState(false);
    const statuses = [
        requirement?.status,
        ...(requirement?.people ?? []).map(({ status }) => status)
    ].join();
    const [previousStatuses, setPreviousStatuses] = useState(statuses);

    // Retrying lasts until anyone's status changes. Clearing it on send would flash the
    // error until the refetch lands.
    if (statuses !== previousStatuses) {
        setPreviousStatuses(statuses);
        setIsRetryingFailedCheck(false);
    }

    if (!requirement) {
        return (
            <Redirect
                to={maintainStateKey({
                    pathname: FUNDING_REQUIREMENTS_LIST_PATH,
                    search
                })}
            />
        );
    }

    // Go by the row, not the people: a row staff override to completed can still have
    // people who read action_needed.
    if (requirement.status === 'completed') {
        return <IdCheckComplete />;
    }

    const people = requirement.people ?? [];
    if (
        people.some(({ status }) => status === 'error') &&
        !isRetryingFailedCheck
    ) {
        return (
            <IdCheckSmsSendError
                isRetrying={false}
                onRetry={() => setIsRetryingFailedCheck(true)}
            />
        );
    }

    if (people.length > 1) {
        return <MultiplePeopleIdCheck people={people} />;
    }

    const person = {
        action_needed: getIdVerificationPersonToVerify,
        in_review: getIdVerificationPersonInReview,
        error: getIdVerificationPersonWithError
    }[requirement.status](requirement);

    if (!person) {
        const message = `IdVerificationPage: nobody to send an ID check SMS for on a requirement that is ${requirement.status}`;
        logError(message, { tags: TAGS.DE_LENDING });
        throw new Error(message);
    }

    if (requirement.status === 'in_review' && !isResending) {
        return (
            <IdCheckSmsSent
                sentNumber={sentNumber}
                onResend={() => setIsResending(true)}
            />
        );
    }

    return (
        <SendIdCheckSms
            people={[person]}
            onSent={({ phone }) => {
                setSentNumber(phone.number);
                setIsResending(false);
            }}
        />
    );
};
